import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";

interface CTAProps {
  type: "ai_training" | "agent_dev" | "fractional_pm" | "random";
  onContactClick?: () => void;
  onCalendarClick?: () => void;
  className?: string;
  blogPost?: any;
  course?: any;
}

interface CTA {
  id: string;
  type: "ai_training" | "agent_dev" | "fractional_pm";
  header: string;
  description: string;
  button_text: string;
  icon: string;
  modal_type: "calendar" | "contact" | null;
  is_active: boolean;
}

export default function DynamicCTA({
  type,
  onContactClick,
  onCalendarClick,
  className = "",
  blogPost,
  course,
}: CTAProps) {
  const [cta, setCta] = useState<CTA | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // This is a simplified version that doesn't rely on Supabase
  // In a real implementation, you would fetch CTAs from your database
  useEffect(() => {
    // Simulate fetching CTA data
    setLoading(true);

    // Determine which type to use
    let ctaType = type;
    if (blogPost?.cta_type) {
      ctaType = blogPost.cta_type;
    } else if (course?.cta_type) {
      ctaType = course.cta_type;
    }

    // Mock CTA data based on type
    setTimeout(() => {
      const mockCTAs = {
        ai_training: {
          id: "1",
          type: "ai_training" as const,
          header: "Master AI Tools with Expert Training",
          description:
            "Join our hands-on workshops and transform the way you work with cutting-edge AI technology.",
          button_text: "View Training Programs",
          icon: "BookOpen",
          modal_type: null,
          is_active: true,
        },
        agent_dev: {
          id: "2",
          type: "agent_dev" as const,
          header: "Custom AI Solutions for Your Business",
          description:
            "Let us build AI agents tailored to your specific needs and workflows.",
          button_text: "Book a Consultation",
          icon: "Bot",
          modal_type: "calendar" as const,
          is_active: true,
        },
        fractional_pm: {
          id: "3",
          type: "fractional_pm" as const,
          header: "Expert Project Leadership Without the Overhead",
          description:
            "Get senior-level project management expertise exactly when you need it.",
          button_text: "Learn More",
          icon: "Briefcase",
          modal_type: "contact" as const,
          is_active: true,
        },
      };

      if (ctaType === "random") {
        const types = ["ai_training", "agent_dev", "fractional_pm"] as const;
        const randomType = types[Math.floor(Math.random() * types.length)];
        setCta(mockCTAs[randomType]);
      } else {
        setCta(mockCTAs[ctaType]);
      }

      setLoading(false);
    }, 300);
  }, [type, blogPost, course]);

  if (loading) {
    return <div className="animate-pulse bg-gray-200 h-48 rounded-lg"></div>;
  }

  if (error || !cta) {
    return null;
  }

  // Get the icon component dynamically
  const IconComponent =
    Icons[cta.icon as keyof typeof Icons] || Icons.ExternalLink;

  // Handle button click based on modal_type
  const handleClick = () => {
    if (cta.modal_type === "calendar" && onCalendarClick) {
      onCalendarClick();
    } else if (cta.modal_type === "contact" && onContactClick) {
      onContactClick();
    }
  };

  // Get the appropriate link based on CTA type
  const getLink = () => {
    switch (cta.type) {
      case "ai_training":
        return "/courses";
      case "agent_dev":
        return "/contact";
      case "fractional_pm":
        return "/fractional-pm";
      default:
        return "/";
    }
  };

  return (
    <div
      className={`bg-gray-50 rounded-lg p-8 border border-gray-200 shadow-sm ${className}`}
    >
      <div className="text-center max-w-3xl mx-auto">
        <h3 className="text-2xl font-semibold text-gray-900 mb-4">
          {cta.header}
        </h3>
        <p className="text-gray-600 mb-6">{cta.description}</p>
        {cta.modal_type ? (
          <button
            onClick={handleClick}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
          >
            <IconComponent className="mr-2 h-5 w-5" />
            {cta.button_text}
          </button>
        ) : (
          <a
            href={getLink()}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-black hover:bg-gray-800 transition-colors duration-200"
          >
            <IconComponent className="mr-2 h-5 w-5" />
            {cta.button_text}
          </a>
        )}
      </div>
    </div>
  );
}
